import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { SnapmonLogo } from '../components/SnapmonLogo';
import { Pill } from '../components/Pill';
import { Icon } from '../components/Icons';
import { authApi } from '../api/auth';
import { useAuthStore } from '../auth/store';
import type { ApiError } from '../api/client';

const Schema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});
type FormValues = z.infer<typeof Schema>;

export function LoginScreen() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [showPw, setShowPw] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    defaultValues: { email: '', password: '' },
  });

  const loginMutation = useMutation({
    mutationFn: (vals: FormValues) => authApi.login(vals.email, vals.password),
    onSuccess: (result) => {
      setSession(result.token, result.creator, result.mustChangePassword);
      navigate(result.mustChangePassword ? '/change-password' : '/', { replace: true });
    },
    onError: (err: ApiError) => {
      setFormError(err?.message ?? 'Login failed');
    },
  });

  const onSubmit = (values: FormValues) => {
    const parsed = Schema.safeParse(values);
    if (!parsed.success) {
      setFormError(parsed.error.errors[0]?.message ?? 'Invalid input');
      return;
    }
    setFormError(null);
    loginMutation.mutate(parsed.data);
  };

  const applyUrl = import.meta.env.VITE_APPLY_TO_JOIN_URL || '#';

  return (
    <div
      style={{ minHeight: '100dvh', display: 'grid', gridTemplateColumns: '1.05fr 1fr', background: 'var(--bg)' }}
      className="snapmon-login-wrap"
    >
      {/* Left brand panel */}
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(165deg, color-mix(in oklab, var(--brand) 18%, var(--bg)), var(--bg) 60%)',
          padding: '36px 44px 36px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          color: 'var(--ink)',
          borderRight: '1px solid var(--line)',
        }}
        className="snapmon-login-stage"
      >
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: 'radial-gradient(60% 60% at 80% 20%, color-mix(in oklab, var(--brand) 35%, transparent), transparent 70%)',
            opacity: 0.6,
          }}
        />
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <SnapmonLogo />
          <a href="https://snapmon.io" style={{ fontSize: 12.5, color: 'var(--ink-3)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            ← Back to snapmon.io
          </a>
        </div>

        <div style={{ position: 'relative', maxWidth: 480 }}>
          <Pill tone="brand" size="md">Creator portal</Pill>
          <h1
            style={{
              margin: '14px 0 8px',
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(34px, 4vw, 48px)',
              fontWeight: 600,
              letterSpacing: '-0.03em',
              lineHeight: 1.05,
              color: 'var(--ink)',
            }}
          >
            Track every code.<br />
            Earn <span style={{ color: 'var(--brand)' }}>for life</span>.
          </h1>
          <p style={{ color: 'var(--ink-2)', fontSize: 14.5, lineHeight: 1.55, maxWidth: 440 }}>
            See how your codes perform, what your subscribers paid you, and exactly when your next automatic payout lands.
          </p>

          <div style={{ marginTop: 26, display: 'grid', gridTemplateColumns: 'repeat(3, auto)', gap: 28 }}>
            {[
              { k: 'Live', v: 'Earnings tracking' },
              { k: 'Forever', v: 'Of every renewal' },
              { k: '1st', v: 'Auto-payout day' },
            ].map((s) => (
              <div key={s.k}>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 22, color: 'var(--ink)', letterSpacing: '-0.02em' }}>{s.k}</div>
                <div style={{ fontSize: 11.5, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 500, marginTop: 2 }}>{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, color: 'var(--ink-3)', fontSize: 12 }}>
          <span>© 2026 Snapmon Studios</span>
          <span style={{ display: 'inline-flex', gap: 16 }}>
            <a href="#" style={{ color: 'inherit' }}>Help</a>
            <a href="#" style={{ color: 'inherit' }}>Terms</a>
            <a href="#" style={{ color: 'inherit' }}>Privacy</a>
          </span>
        </div>
      </div>

      {/* Right form panel */}
      <div
        style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '36px 28px' }}
        className="snapmon-login-form-wrap"
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{ width: '100%', maxWidth: 380, display: 'flex', flexDirection: 'column', gap: 16 }}
        >
          <div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600 }}>Sign in</div>
            <h2 style={{ margin: '4px 0 6px', fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 600, letterSpacing: '-0.02em' }}>
              Welcome back
            </h2>
            <p style={{ color: 'var(--ink-3)', fontSize: 13, margin: 0 }}>
              Sign in with your creator account to view your earnings.
            </p>
          </div>

          <Field label="Email" error={errors.email?.message}>
            <input
              {...register('email')}
              autoComplete="email"
              autoFocus
              placeholder="you@example.com"
              style={inputStyle}
            />
          </Field>

          <Field
            label="Password"
            error={errors.password?.message}
            hint={
              <a
                href="mailto:support@snapmon.io?subject=Creator%20password%20reset"
                style={{ fontSize: 12, color: 'var(--brand)', fontWeight: 500, marginLeft: 'auto' }}
                title="Email support and we'll reset your password"
              >
                Forgot password?
              </a>
            }
            right={
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                style={{ fontSize: 11, color: 'var(--ink-3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '4px 6px' }}
              >
                {showPw ? 'Hide' : 'Show'}
              </button>
            }
          >
            <input
              {...register('password')}
              type={showPw ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              style={{
                ...inputStyle,
                fontFamily: showPw ? 'var(--font-body)' : 'var(--font-mono)',
                letterSpacing: showPw ? 'normal' : '0.18em',
              }}
            />
          </Field>

          {formError && (
            <div
              style={{
                fontSize: 12.5,
                color: 'var(--bad)',
                background: 'color-mix(in oklab, var(--bad) 10%, var(--bg-elev))',
                border: '1px solid color-mix(in oklab, var(--bad) 25%, transparent)',
                padding: '8px 12px',
                borderRadius: 'var(--r-sm)',
              }}
            >
              {formError}
            </div>
          )}

          <button
            type="submit"
            disabled={loginMutation.isPending}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              background: 'var(--brand)',
              color: '#fff',
              padding: '12px 16px',
              borderRadius: 'var(--r-md)',
              fontWeight: 600,
              fontSize: 14,
              cursor: loginMutation.isPending ? 'progress' : 'pointer',
              opacity: loginMutation.isPending ? 0.85 : 1,
              transition: 'opacity 0.15s ease',
            }}
          >
            {loginMutation.isPending ? (
              <>
                <span className="snapmon-spinner" />
                Signing in…
              </>
            ) : (
              <>
                Sign in
                <Icon.Arrow />
              </>
            )}
          </button>

          <div style={{ marginTop: 18, fontSize: 13, color: 'var(--ink-3)', textAlign: 'center' }}>
            New to Snapmon's affiliate program?{' '}
            <a href={applyUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--brand)', fontWeight: 600 }}>
              Apply to join
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputStyle = {
  flex: 1,
  minWidth: 0,
  border: 0,
  background: 'transparent',
  outline: 'none',
  fontSize: 14,
  color: 'var(--ink)',
} as const;

function Field({
  label,
  children,
  hint,
  right,
  error,
}: {
  label: string;
  children: React.ReactNode;
  hint?: React.ReactNode;
  right?: React.ReactNode;
  error?: string;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 11.5, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
          {label}
        </span>
        {hint}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'var(--bg-elev)',
          border: '1px solid ' + (error ? 'var(--bad)' : 'var(--line-strong)'),
          borderRadius: 'var(--r-md)',
          padding: '10px 12px',
          transition: 'border-color 0.12s ease, box-shadow 0.12s ease',
        }}
      >
        {children}
        {right}
      </div>
      {error && <span style={{ fontSize: 11, color: 'var(--bad)' }}>{error}</span>}
    </label>
  );
}
